/**
 * Footwear collection/search cards: lift near-grey studio backgrounds to white
 * and trim empty margins so side-facing shoes share a sole baseline.
 * Only runs on .card-gallery--footwear / .product-card--footwear images.
 * Does not rewrite files on Shopify â canvas is display-only.
 */
const SELECTOR =
  '.card-gallery--footwear img.product-media__image, .product-card--footwear img.product-media__image';
const DONE = 'done';
const PENDING = 'pending';
const SKIP = 'skip';
const MAX_EDGE = 900;
const BRIGHTNESS_MIN = 0.82;
const SATURATION_MAX = 0.12;
const BG_CHANNEL_TOLERANCE = 18;
const TRIM_WHITE = 250;
const TRIM_ROW_RATIO = 0.992;
const PAD_RATIO = 0.02;
const MIN_KEEP_RATIO = 0.3;

/**
 * @param {number} r
 * @param {number} g
 * @param {number} b
 */
function saturation(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max === 0 ? 0 : (max - min) / max;
}

/**
 * @param {number} r
 * @param {number} g
 * @param {number} b
 */
function isLightNeutral(r, g, b) {
  const brightness = Math.max(r, g, b) / 255;
  return brightness >= BRIGHTNESS_MIN && saturation(r, g, b) <= SATURATION_MAX;
}

/**
 * @param {Uint8ClampedArray} data
 * @param {number} width
 * @param {number} height
 */
function sampleStudioBackground(data, width, height) {
  const inset = 4;
  const spots = [
    [inset, inset],
    [width - 1 - inset, inset],
    [inset, height - 1 - inset],
    [width - 1 - inset, height - 1 - inset],
    [Math.floor(width / 2), inset],
    [inset, Math.floor(height / 2)],
  ];

  let r = 0;
  let g = 0;
  let b = 0;
  let hits = 0;

  for (const [x, y] of spots) {
    const i = (y * width + x) * 4;
    const sr = data[i];
    const sg = data[i + 1];
    const sb = data[i + 2];
    if (isLightNeutral(sr, sg, sb)) {
      r += sr;
      g += sg;
      b += sb;
      hits += 1;
    }
  }

  if (hits < 4) return null;
  return {
    r: Math.round(r / hits),
    g: Math.round(g / hits),
    b: Math.round(b / hits),
  };
}

/**
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @param {{r: number, g: number, b: number}} bg
 */
function matchesBackground(r, g, b, bg) {
  if (!isLightNeutral(r, g, b)) return false;
  return (
    Math.abs(r - bg.r) <= BG_CHANNEL_TOLERANCE &&
    Math.abs(g - bg.g) <= BG_CHANNEL_TOLERANCE &&
    Math.abs(b - bg.b) <= BG_CHANNEL_TOLERANCE
  );
}

/**
 * @param {Uint8ClampedArray} data
 * @param {number} width
 * @param {number} height
 */
function trimWhiteBounds(data, width, height) {
  const isWhite = (x, y) => {
    const i = (y * width + x) * 4;
    return data[i] >= TRIM_WHITE && data[i + 1] >= TRIM_WHITE && data[i + 2] >= TRIM_WHITE;
  };

  const rowWhite = (y) => {
    let n = 0;
    for (let x = 0; x < width; x += 1) {
      if (isWhite(x, y)) n += 1;
    }
    return n / width >= TRIM_ROW_RATIO;
  };

  const colWhite = (x) => {
    let n = 0;
    for (let y = 0; y < height; y += 1) {
      if (isWhite(x, y)) n += 1;
    }
    return n / height >= TRIM_ROW_RATIO;
  };

  let top = 0;
  let bottom = height - 1;
  let left = 0;
  let right = width - 1;

  while (top < bottom && rowWhite(top)) top += 1;
  while (bottom > top && rowWhite(bottom)) bottom -= 1;
  while (left < right && colWhite(left)) left += 1;
  while (right > left && colWhite(right)) right -= 1;

  const pad = Math.max(2, Math.round(Math.min(width, height) * PAD_RATIO));
  top = Math.max(0, top - pad);
  left = Math.max(0, left - pad);
  bottom = Math.min(height - 1, bottom + pad);
  right = Math.min(width - 1, right + pad);

  const keepW = right - left + 1;
  const keepH = bottom - top + 1;
  if (keepW / width < MIN_KEEP_RATIO || keepH / height < MIN_KEEP_RATIO) {
    return { x: 0, y: 0, w: width, h: height };
  }

  return { x: left, y: top, w: keepW, h: keepH };
}

/**
 * @param {HTMLImageElement} img
 * @returns {Promise<void>}
 */
async function processImage(img) {
  const src = img.currentSrc || img.src;
  if (!src || src.startsWith('blob:') || src.startsWith('data:')) {
    img.dataset.footwearMedia = SKIP;
    return;
  }

  if (!img.complete || img.naturalWidth === 0) {
    await new Promise((resolve, reject) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', reject, { once: true });
    });
  }

  const probe = new Image();
  probe.crossOrigin = 'anonymous';
  probe.decoding = 'async';

  await new Promise((resolve, reject) => {
    probe.onload = resolve;
    probe.onerror = reject;
    probe.src = img.currentSrc || img.src;
  });

  const scale = Math.min(1, MAX_EDGE / Math.max(probe.naturalWidth, probe.naturalHeight));
  const width = Math.max(1, Math.round(probe.naturalWidth * scale));
  const height = Math.max(1, Math.round(probe.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    img.dataset.footwearMedia = SKIP;
    return;
  }

  ctx.drawImage(probe, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const bg = sampleStudioBackground(data, width, height);

  if (bg) {
    for (let i = 0; i < data.length; i += 4) {
      if (matchesBackground(data[i], data[i + 1], data[i + 2], bg)) {
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }

  const bounds = trimWhiteBounds(data, width, height);
  const trimmed = bounds.x > 0 || bounds.y > 0 || bounds.w < width || bounds.h < height;
  if (!bg && !trimmed) {
    img.dataset.footwearMedia = DONE;
    return;
  }

  const out = document.createElement('canvas');
  out.width = bounds.w;
  out.height = bounds.h;
  const outCtx = out.getContext('2d');
  if (!outCtx) {
    img.dataset.footwearMedia = SKIP;
    return;
  }
  outCtx.fillStyle = '#fff';
  outCtx.fillRect(0, 0, bounds.w, bounds.h);
  outCtx.drawImage(canvas, bounds.x, bounds.y, bounds.w, bounds.h, 0, 0, bounds.w, bounds.h);

  const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', 0.9));
  if (!blob) {
    img.dataset.footwearMedia = SKIP;
    return;
  }

  const prev = img.dataset.footwearBlob;
  const url = URL.createObjectURL(blob);
  img.dataset.footwearMedia = DONE;
  img.dataset.footwearBlob = url;
  img.removeAttribute('srcset');
  img.removeAttribute('sizes');
  img.src = url;
  if (prev) URL.revokeObjectURL(prev);
}

/**
 * @param {Element | null | undefined} img
 */
function queue(img) {
  if (!(img instanceof HTMLImageElement)) return;
  if (img.dataset.footwearMedia === DONE || img.dataset.footwearMedia === PENDING) return;
  img.dataset.footwearMedia = PENDING;

  const run = () => {
    processImage(img).catch(() => {
      img.dataset.footwearMedia = SKIP;
    });
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(run, { timeout: 1800 });
  } else {
    setTimeout(run, 0);
  }
}

/**
 * @param {ParentNode} [root]
 */
function scan(root = document) {
  root.querySelectorAll(SELECTOR).forEach(queue);
}

scan();

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === 'attributes' && mutation.target instanceof HTMLImageElement) {
      const img = mutation.target;
      if (!img.matches(SELECTOR)) continue;
      const src = img.src || '';
      if (src.startsWith('blob:') || src.startsWith('data:')) continue;
      if (img.dataset.footwearMedia === PENDING) continue;
      img.dataset.footwearMedia = '';
      queue(img);
      continue;
    }

    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(SELECTOR)) queue(node);
      else scan(node);
    }
  }
});

observer.observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['src'],
});